/**
 * Status bar component for displaying minimized backup/restore operations.
 * Appears at the bottom of the screen when operations are minimized.
 * Clicking "restore" opens a details dialog for that operation.
 */

import { useState } from 'react';
import { Database, X, Maximize2, CheckCircle, XCircle } from 'lucide-react';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import { Progress } from '../../../components/ui/progress';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { ProgressIndicator } from './ProgressIndicator';
import type { BackupTaskStatus, ActiveBackupOperation } from '../types/backupRestore';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
function getStatusIcon(status: BackupTaskStatus) {
  switch (status) {
    case 'succeeded':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-destructive" />;
    case 'cancelled':
      return <X className="h-4 w-4 text-muted-foreground" />;
    default:
      return <RandomDotmSpinner size={16} className="text-primary" />;
  }
}

function isTerminalStatus(status: BackupTaskStatus): boolean {
  return status === 'succeeded' || status === 'failed' || status === 'cancelled';
}

/** Dialog showing operation details when restored from status bar */
function OperationDetailsDialog({
  operation,
  open,
  onClose,
}: {
  operation: ActiveBackupOperation;
  open: boolean;
  onClose: () => void;
}) {
  const { removeOperation, minimizeOperation } = useBackupRestoreStore();
  const isComplete = isTerminalStatus(operation.status);

  const handleMinimize = () => {
    minimizeOperation(operation.id);
    onClose();
  };

  const handleDone = () => {
    removeOperation(operation.id);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent data-testid="operation-details-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>
                {operation.type === 'export' ? 'Export' : 'Import'} Data-tier Application
              </DialogTitle>
              <DialogDescription className="mt-1">
                {operation.type === 'export' ? 'Exporting' : 'Importing'} database &quot;{operation.databaseName}&quot;
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <ProgressIndicator
            status={operation.status}
            progress={operation.progress}
            message={operation.message}
            error={operation.error}
            data-testid="operation-details-progress"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {!isComplete && (
            <Button
              variant="outline"
              onClick={handleMinimize}
              data-testid="operation-details-minimize"
            >
              Minimize
            </Button>
          )}
          <Button
            variant={isComplete ? 'default' : 'outline'}
            onClick={isComplete ? handleDone : handleMinimize}
            data-testid="operation-details-close"
          >
            {isComplete ? 'Done' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BackupStatusBar() {
  // Select raw state (not derived) to avoid infinite re-render loops
  const operations = useBackupRestoreStore((s) => s.operations);
  const minimizedOperationIds = useBackupRestoreStore((s) => s.minimizedOperationIds);
  const restoreOperation = useBackupRestoreStore((s) => s.restoreOperation);
  const removeOperation = useBackupRestoreStore((s) => s.removeOperation);

  // Compute minimized operations outside the selector
  const minimizedOps = operations.filter((op) => minimizedOperationIds.has(op.id));

  // Track which operation to show in detail dialog
  const [detailsOperationId, setDetailsOperationId] = useState<string | null>(null);
  const detailsOperation = detailsOperationId
    ? operations.find((op) => op.id === detailsOperationId)
    : null;

  const handleRestore = (operationId: string) => {
    restoreOperation(operationId);
    setDetailsOperationId(operationId);
  };

  const handleCloseDetails = () => {
    setDetailsOperationId(null);
  };

  if (minimizedOps.length === 0 && !detailsOperation) {
    return null;
  }

  return (
    <>
      {/* Status bar */}
      {minimizedOps.length > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-background border-t border-border px-4 py-2 z-50"
          data-testid="backup-status-bar"
        >
          <div className="flex items-center gap-4 overflow-x-auto">
            {minimizedOps.map((op) => {
              const isComplete = isTerminalStatus(op.status);
              const progressValue = op.progress ?? (isComplete ? 100 : 0);

              return (
                <div
                  key={op.id}
                  className="flex items-center gap-2 bg-muted/50 rounded-md px-3 py-1.5 min-w-[200px] max-w-[300px]"
                  data-testid={`backup-status-item-${op.id}`}
                >
                  {/* Icon */}
                  <Database className="h-4 w-4 text-primary flex-shrink-0" />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium truncate">
                        {op.type === 'export' ? 'Exporting' : 'Importing'} {op.databaseName}
                      </span>
                      {getStatusIcon(op.status)}
                    </div>
                    {!isComplete && (
                      <Progress value={progressValue} className="h-1 mt-1" />
                    )}
                    {op.message && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {op.message}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleRestore(op.id)}
                          data-testid={`backup-status-restore-${op.id}`}
                        >
                          <Maximize2 className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Show details</TooltipContent>
                    </Tooltip>

                    {isComplete && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => removeOperation(op.id)}
                            data-testid={`backup-status-dismiss-${op.id}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Dismiss</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Details dialog */}
      {detailsOperation && (
        <OperationDetailsDialog
          operation={detailsOperation}
          open={true}
          onClose={handleCloseDetails}
        />
      )}
    </>
  );
}
